import type { Coupon, SROW } from '../../../schema/coupon/entry.js';
import type { Definition } from '../../../schema/frameworks/entry.js';
import type { ResultFilter } from '../../lib/entry.js';
import { expandCouponToSROW } from './systems/expandCouponToSROW.js';

const calculateDistributions = (distributionsMatrix: number[][]): number[][] =>
  distributionsMatrix.map((row) => {
    const total = row.reduce((sum, col) => sum + col, 0);
    return row.map((col) => Math.round(Number.parseFloat((col / total).toPrecision(2)) * 100));
  });
export interface IBestBetRowsEntity {
  expandToSrow(): SROW[];
  totalNumberOfRows(): number;
  calculateBestBetRows(resultSrow: ResultFilter): SROW[][];
  calculateOutcomeDistribution(outcomeCount: number): number[][];
}

export class BestBetRowsEntity implements IBestBetRowsEntity {
  private readonly _coupon: Coupon;
  private readonly _definition: Definition;

  private _srowCache: SROW[] | undefined = undefined;
  private _outcomeDistributionCache: number[][] | undefined = undefined;
  private _totalNumberOfRowsCache: number | undefined = undefined;

  constructor(model: Coupon, definition: Definition) {
    this._coupon = model;
    this._definition = definition;
  }

  expandToSrow(): SROW[] {
    if (!this._srowCache) {
      this._srowCache = expandCouponToSROW(this._coupon, { definition: this._definition });
    }

    return this._srowCache;
  }

  totalNumberOfRows(): number {
    if (!this._totalNumberOfRowsCache) {
      this._totalNumberOfRowsCache = expandCouponToSROW(this._coupon, { definition: this._definition }).length;
    }

    return this._totalNumberOfRowsCache;
  }

  calculateBestBetRows(resultSrow: ResultFilter): SROW[][] {
    const srows = this.expandToSrow();
    const totalWinDivisions = resultSrow.length;

    if (totalWinDivisions === 0) {
      return [srows];
    }
    // Add one for 0 corrects
    const sortedWinDivisions: SROW[][] = Array.from({ length: totalWinDivisions + 1 }, () => [] as SROW[]);

    for (const srow of srows) {
      const correctOutcomes = srow.filter((outcome, index) => {
        const resultOutcome = resultSrow[index];

        if (!resultOutcome) {
          // insufficient row or masked
          return false;
        }

        return outcome.toString() === resultOutcome.toString();
      });

      sortedWinDivisions[totalWinDivisions - correctOutcomes.length]?.push(srow);
    }

    return sortedWinDivisions;
  }

  calculateOutcomeDistribution(outcomeCount: number): number[][] {
    if (this._outcomeDistributionCache) {
      return this._outcomeDistributionCache;
    }
    const srows = this.expandToSrow();
    const distributionsMatrix: number[][] = [];

    for (const srow of srows) {
      for (const [rowNum, colNum] of srow.entries()) {
        if (!distributionsMatrix[rowNum]) {
          distributionsMatrix[rowNum] = Array.from<number>({ length: outcomeCount }).fill(0);
        }
        const colIndex = colNum.pop();
        if (colIndex === undefined) {
          continue;
        }
        const row = distributionsMatrix[rowNum];
        const current = row[colIndex] ?? 0;
        row[colIndex] = current + 1;
      }
    }
    this._outcomeDistributionCache = calculateDistributions(distributionsMatrix);
    return this._outcomeDistributionCache;
  }
}
